import React, { useState } from "react";
import { useI18n } from "../i18n";
import Reveal from "../components/Reveal";
import axios from "axios";
import { Send, CheckCircle2, Loader2, Mail, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Eyebrow = ({ children }) => (
  <div className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] text-[#00B3B3]/90 uppercase">
    <span className="h-px w-8 bg-[#00B3B3]/60" />
    {children}
  </div>
);

export default function Contact() {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      setError(err?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-contact" className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <Reveal><Eyebrow>{t.contact.eyebrow}</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 font-heading font-black tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              {t.contact.title}
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-white/65 leading-relaxed max-w-md">{t.contact.sub}</p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-10 space-y-4">
              <div className="flex items-center gap-3 text-white/80">
                <Mail size={16} className="text-[#00B3B3]" />
                <a href="mailto:geral@beyond.com.pt" className="hover:text-[#00B3B3]">
                  geral@beyond.com.pt
                </a>
              </div>
              <div className="flex items-center gap-3 text-white/80">
                <MapPin size={16} className="text-[#00B3B3]" />
                <span>Lisboa · Portugal</span>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.2}>
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="border border-white/10 bg-[#0a0a0a] p-8 md:p-10 space-y-5"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block font-mono text-[10px] tracking-widest text-white/40 mb-2">{t.contact.name}</label>
                  <input
                    required
                    data-testid="contact-name"
                    value={form.name}
                    onChange={handle("name")}
                    className="w-full bg-[#111] border border-white/10 text-white px-4 py-3 rounded-sm outline-none focus:border-[#00B3B3] transition-colors"
                  />
                </div>
                <div>
                  <label className="block font-mono text-[10px] tracking-widest text-white/40 mb-2">{t.contact.email}</label>
                  <input
                    required
                    type="email"
                    data-testid="contact-email"
                    value={form.email}
                    onChange={handle("email")}
                    className="w-full bg-[#111] border border-white/10 text-white px-4 py-3 rounded-sm outline-none focus:border-[#00B3B3] transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block font-mono text-[10px] tracking-widest text-white/40 mb-2">{t.contact.company}</label>
                <input
                  data-testid="contact-company"
                  value={form.company}
                  onChange={handle("company")}
                  className="w-full bg-[#111] border border-white/10 text-white px-4 py-3 rounded-sm outline-none focus:border-[#00B3B3] transition-colors"
                />
              </div>
              <div>
                <label className="block font-mono text-[10px] tracking-widest text-white/40 mb-2">{t.contact.message}</label>
                <textarea
                  required
                  rows={5}
                  data-testid="contact-message"
                  value={form.message}
                  onChange={handle("message")}
                  className="w-full bg-[#111] border border-white/10 text-white px-4 py-3 rounded-sm outline-none focus:border-[#00B3B3] transition-colors resize-none"
                />
              </div>

              {error && (
                <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/10 px-4 py-2">
                  {error}
                </div>
              )}

              {done ? (
                <div data-testid="contact-success" className="flex items-center gap-3 text-[#39FF14] border border-[#39FF14]/30 bg-[#39FF14]/5 px-4 py-3">
                  <CheckCircle2 size={18} /> {t.contact.sent}
                </div>
              ) : (
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={loading}
                  className="inline-flex items-center gap-2 bg-[#00B3B3] text-white font-semibold px-6 py-3 rounded-sm hover:bg-[#00CCCC] transition-colors disabled:opacity-60"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  {t.contact.send}
                </button>
              )}
            </form>
          </Reveal>
        </div>
      </div>
    </div>
  );
}
